import React, { useEffect, useMemo, useState } from "react";
import { formatMoney, getCurrencySymbol, resolveCurrencyContext } from "../utils/currency";
import {
  resolveTaxContractFromProfile,
  resolveTaxLabelFromProfile,
} from "../utils/taxDetails";
import { API_ENDPOINTS, API_BASE_URL } from "../config/constants";
import { getCachedProducts } from "../utils/productListCache";
import {
  enqueueCategoryMutation,
  enqueueProductMutation,
  enqueueProductRequest,
  getCachedCategories,
  persistCategoryCaches,
  persistProductCaches,
} from "../utils/productSyncQueue";
import { dedupeProductsById, mapWithConcurrency } from "../utils/productDeduplication";
import {
  getFullOfflineModeEnabled,
  isFullOfflineModeEnabled,
} from "../utils/fullOfflineMode";
import {
  createOfflineCategory,
  listOfflineCategories,
} from "../offline/categoryStore";
import {
  createOfflineProduct,
  deleteOfflineProduct,
  hasOfflineProductStore,
  listOfflineProducts,
  updateOfflineProduct,
} from "../offline/productStore";

const BRAND = "var(--app-primary)";
const PRICE_INPUT_MAX_CHARS = 5;
const DESKTOP_CACHE_HEADER = "x-billsarthi-desktop-cache";

const sanitizePriceInput = (raw, maxChars = PRICE_INPUT_MAX_CHARS) => {
  const text = String(raw || "").replace(/[^\d.]/g, "");
  const firstDot = text.indexOf(".");
  const normalized =
    firstDot === -1
      ? text
      : `${text.slice(0, firstDot + 1)}${text.slice(firstDot + 1).replace(/\./g, "")}`;
  return normalized.slice(0, maxChars);
};

const getProductId = (item) => {
  const raw = item?.id ?? item?.productid ?? item?.product_id;
  const id = Number(raw);
  return Number.isFinite(id) ? id : null;
};

const getProductKey = (item) => String(
  item?.id ?? item?.productid ?? item?.product_id ?? ""
).trim();

const getUsedDeleteTargets = (target) =>
  target?.__bulk === true && Array.isArray(target.products)
    ? target.products
    : target
      ? [target]
      : [];

const getPersistedVariantId = (variant) => {
  const id = Number(variant?.variantid);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const AddProduct = ({ embedded = false, onSaved, onCancel }) => {
  const currencyContext = resolveCurrencyContext(
    JSON.parse(localStorage.getItem("userData") || "{}")
  );
  const currencySymbol = getCurrencySymbol(currencyContext);
  const fmtMoney = (value) => formatMoney(value, currencyContext);
  const [categoryList, setCategoryList] = useState([]);
  const [productList, setProductList] = useState([]);

  // form
  const [selectedCategory, setSelectedCategory] = useState("");
  const [productName, setProductName] = useState("");
  const [price, setPrice] = useState("");
  const [priceTouched, setPriceTouched] = useState(false);
  const [gstEnabled, setGstEnabled] = useState(false);
  const [gstPercent, setGstPercent] = useState("");
  const [taxChoice, setTaxChoice] = useState(""); // withtax | withouttax
  const [editingProductId, setEditingProductId] = useState(null);
  const [statusValue, setStatusValue] = useState("1"); // 1=active,0=inactive
  const [displayOrder, setDisplayOrder] = useState("0");
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryItemsLoading, setInventoryItemsLoading] = useState(false);
  const [inventoryLinkEnabled, setInventoryLinkEnabled] = useState(false);
  const [selectedInventoryItemId, setSelectedInventoryItemId] = useState("");

  // variants
  const [variantsEnabled, setVariantsEnabled] = useState(false);
  const [variants, setVariants] = useState([]);
  const [defaultVariantId, setDefaultVariantId] = useState(null);
  const [loadedVariantsSnapshot, setLoadedVariantsSnapshot] = useState([]);

  // add-ons
  const [addonsEnabled, setAddonsEnabled] = useState(false);
  const [addonList, setAddonList] = useState([]);
  const [addonLoading, setAddonLoading] = useState(false);
  const [selectedAddonIds, setSelectedAddonIds] = useState([]);
  const [newAddonName, setNewAddonName] = useState("");
  const [newAddonPrice, setNewAddonPrice] = useState("");
  const [savingOptions, setSavingOptions] = useState(false);
  const [editingAddonId, setEditingAddonId] = useState(null);
  const [editAddonName, setEditAddonName] = useState("");
  const [editAddonPrice, setEditAddonPrice] = useState("");
  const [savingAddonEdit, setSavingAddonEdit] = useState(false);

  // add category modal
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);

  // image (new)
  const [productImageFile, setProductImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);

  // list tools
  const [productsPerPage, setProductsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [usedProductDeleteTarget, setUsedProductDeleteTarget] = useState(null);
  const [usedProductDeleteAgreed, setUsedProductDeleteAgreed] = useState(false);

  // ui
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null); // {type,msg}

  const userData = JSON.parse(localStorage.getItem("userData"));
  const restaurantProfile = userData?.data || {};
  const restaurantId = userData?.data?.id || 1;
  const taxContract = resolveTaxContractFromProfile(restaurantProfile);
  const genericTax = taxContract === "generic_tax_v2";
  const taxDisplayLabel = resolveTaxLabelFromProfile(restaurantProfile);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2200);
  };

  const isDesktopOffline = () =>
    Boolean(window.desktopOfflineMutations || window.desktopProductQueue) &&
    (isFullOfflineModeEnabled() || navigator.onLine === false);

  const readOfflineFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      __offlineFile: true,
      name: file.name || "product-image",
      type: file.type || "application/octet-stream",
      dataUrl: String(reader.result || ""),
    });
    reader.onerror = () => reject(reader.error || new Error("Unable to save product image offline"));
    reader.readAsDataURL(file);
  });

  const saveProductListLocally = async (next) => {
    const uniqueProducts = dedupeProductsById(next);
    setProductList(uniqueProducts);
    await persistProductCaches(restaurantId, uniqueProducts);
  };

  const applyOfflineProducts = (products) => {
    const records = Array.isArray(products) ? products : [];
    setProductList(records);
    try { localStorage.setItem("productlistx", JSON.stringify(records)); } catch {}
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([
        fetchCategories(),
        fetchProducts(),
        fetchInventoryItems(),
      ]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async () => {
    if (isFullOfflineModeEnabled()) {
      try {
        const categories = await listOfflineCategories(restaurantId);
        setCategoryList(categories);
        try { localStorage.setItem("categorylistx", JSON.stringify(categories)); } catch {}
      } catch (error) {
        showToast("error", error?.message || "Unable to load Full Offline categories");
      }
      return;
    }
    const cachedCategories = await getCachedCategories(restaurantId);
    if (cachedCategories.length) setCategoryList(cachedCategories);
    try {
      const res = await fetch(
        API_ENDPOINTS.LIST_PRODUCT_CATEGORY,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId }),
        }
      );
      const data = await res.json();
      if (
        res.headers.get(DESKTOP_CACHE_HEADER) === "1" &&
        cachedCategories.some((category) => category?.__offlinePending)
      ) {
        setCategoryList(cachedCategories);
        return;
      }
      const list = Array.isArray(data?.list) ? data.list : [];
      setCategoryList(list);
      await persistCategoryCaches(restaurantId, list);
    } catch (e) {
      if (!cachedCategories.length) showToast("error", e?.message || "Failed to load categories");
    }
  };

  const fetchProducts = async () => {
    if (isFullOfflineModeEnabled()) {
      try {
        const products = await listOfflineProducts(restaurantId);
        applyOfflineProducts(products);
      } catch (error) {
        showToast("error", error?.message || "Unable to load Full Offline products");
      }
      return;
    }
    try {
      const cachedProducts = await getCachedProducts(restaurantId, "master");
      const uniqueCachedProducts = dedupeProductsById(cachedProducts);
      if (uniqueCachedProducts.length) setProductList(uniqueCachedProducts);
      const res = await fetch(
        API_ENDPOINTS.PRODUCT_LIST_MASTER,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId }),
        }
      );
      const data = await res.json();
      const returnedFromApiCache = res.headers.get(DESKTOP_CACHE_HEADER) === "1";
      if (returnedFromApiCache && Array.isArray(cachedProducts)) {
        // An older cached HTTP response must not replace newer offline edits.
        setProductList(uniqueCachedProducts);
        console.info("[Product offline debug] kept newer master SQLite cache", {
          restaurantId: String(restaurantId), count: uniqueCachedProducts.length,
        });
        return;
      }
      const uniqueProducts = dedupeProductsById(data.list);
      setProductList(uniqueProducts);
      // Keep Add Product, POS and browser fallback on the same latest product data.
      if (Array.isArray(data.list)) await persistProductCaches(restaurantId, uniqueProducts);
    } catch (e) {
      showToast("error", e?.message || "Failed to load products");
    }
  };

  const fetchInventoryItems = async () => {
    if (!restaurantId) return;
    if (isFullOfflineModeEnabled()) {
      setInventoryItems([]);
      setInventoryItemsLoading(false);
      return;
    }
    try {
      setInventoryItemsLoading(true);
      const res = await fetch(
        `${API_BASE_URL}/inventory/items?restaurantid=${encodeURIComponent(
          restaurantId
        )}&only_active=0`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      const result = await res.json();
      if (res.ok && result?.success) {
        setInventoryItems(Array.isArray(result?.items) ? result.items : []);
      } else {
        showToast(
          "error",
          result?.message || "Failed to load inventory items"
        );
      }
    } catch (e) {
      showToast("error", e?.message || "Failed to load inventory items");
    } finally {
      setInventoryItemsLoading(false);
    }
  };

  const inventoryPickerItems = useMemo(
    () =>
      (Array.isArray(inventoryItems) ? inventoryItems : []).filter(
        (item) =>
          String(item?.base_uom || "").trim().toLowerCase() === "pcs"
      ),
    [inventoryItems]
  );

  // variants helpers
  const addVariantRow = () => {
    const id = Date.now() + Math.floor(Math.random() * 1000);
    setVariants((prev) => [
      ...prev,
      { id, variantid: null, label: "", price: "" },
    ]);
    if (!defaultVariantId) {
      setDefaultVariantId(id);
    }
  };

  const updateVariantField = (id, field, value) => {
    const nextValue = field === "price" ? sanitizePriceInput(value) : value;
    setVariants((prev) =>
      prev.map((variant) =>
        variant.id === id ? { ...variant, [field]: nextValue } : variant
      )
    );
  };

  const removeVariantRow = (id) => {
    setVariants((prev) => {
      const next = prev.filter((variant) => variant.id !== id);
      if (defaultVariantId === id) {
        setDefaultVariantId(next[0]?.id || null);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!variantsEnabled) return;
    if (variants.length === 0) {
      addVariantRow();
    }
  }, [variantsEnabled, variants.length]);

  useEffect(() => {
    if (!variantsEnabled || priceTouched) return;
    const fallback =
      variants.find((v) => v.id === defaultVariantId) || variants[0];
    const nextPrice = fallback?.price ?? "";
    if (nextPrice && !price) {
      setPrice(String(nextPrice));
    }
  }, [variantsEnabled, variants, defaultVariantId, priceTouched, price]);

  // addons helpers
  const fetchAddonList = async (restaurantId) => {
    if (!restaurantId) return;
    if (isFullOfflineModeEnabled()) return;
    try {
      setAddonLoading(true);
      const res = await fetch(
        API_ENDPOINTS.PRODUCT_ADDONS_LIST,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId }),
        }
      );
      const result = await res.json();
      if (res.ok && result?.success) {
        setAddonList(result.addons || []);
      } else {
        showToast("error", result?.message || "Failed to load addons");
      }
    } catch (e) {
      showToast("error", e?.message || "Failed to load addons");
    } finally {
      setAddonLoading(false);
    }
  };

  useEffect(() => {
    if (!addonsEnabled || !restaurantId) return;
    fetchAddonList(restaurantId);
  }, [addonsEnabled, restaurantId]);

  const toggleAddonSelection = (addonId) => {
    setSelectedAddonIds((prev) => {
      const exists = prev.includes(addonId);
      if (exists) {
        return prev.filter((id) => id !== addonId);
      }
      return [...prev, addonId];
    });
  };

  const startEditAddon = (addon) => {
    if (!addon?.id) return;
    setEditingAddonId(addon.id);
    setEditAddonName(addon?.name || "");
    const basePrice = addon?.price ?? addon?.priceaftertax ?? 0;
    setEditAddonPrice(String(basePrice));
  };

  const cancelEditAddon = () => {
    setEditingAddonId(null);
    setEditAddonName("");
    setEditAddonPrice("");
  };

  const handleUpdateAddon = async () => {
    if (!editingAddonId || !restaurantId) return;
    const name = (editAddonName || "").trim();
    const basePrice = Number(editAddonPrice || 0);
    if (!name) {
      showToast("info", "Enter addon name");
      return;
    }
    if (!Number.isFinite(basePrice) || basePrice < 0) {
      showToast("info", "Enter valid addon price");
      return;
    }

    try {
      setSavingAddonEdit(true);
      const taxPayload = buildTaxPayload(basePrice);
      const fields = {
        restaurantid: restaurantId,
        addonid: editingAddonId,
        name,
        price: basePrice,
        priceaftertax: taxPayload.priceaftertax,
        taxamount: taxPayload.taxamount,
        sgst: taxPayload.sgst,
        cgst: taxPayload.cgst,
        taxedprice: taxPayload.taxedprice,
      };
      const applyLocalUpdate = () => {
        const localOnly = isFullOfflineModeEnabled();
        setAddonList((prev) =>
          prev.map((addon) => addon.id === editingAddonId ? {
            ...addon,
            ...fields,
            __offlinePending: !localOnly,
            __localOnly: localOnly,
          } : addon)
        );
        cancelEditAddon();
      };
      if (isDesktopOffline()) {
        await enqueueProductRequest({ restaurantId, operation: "addon-save", endpoint: API_ENDPOINTS.PRODUCT_ADDONS_SAVE, fields });
        applyLocalUpdate();
        showToast("success", isFullOfflineModeEnabled()
          ? "Addon saved in the Full Offline database. It will not sync online."
          : "Addon saved offline. It will sync automatically.");
        return;
      }
      const res = await fetch(
        API_ENDPOINTS.PRODUCT_ADDONS_SAVE,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        }
      );
      const result = await res.json();
      if (res.ok && result?.success) {
        const updated = result?.addon || {
          id: editingAddonId,
          name,
          price: basePrice,
          priceaftertax: taxPayload.priceaftertax,
          taxamount: taxPayload.taxamount,
          sgst: taxPayload.sgst,
          cgst: taxPayload.cgst,
          taxedprice: taxPayload.taxedprice,
        };
        setAddonList((prev) =>
          prev.map((addon) =>
            addon.id === editingAddonId ? { ...addon, ...updated } : addon
          )
        );
        cancelEditAddon();
        showToast("success", "Addon updated");
      } else {
        showToast("error", result?.message || "Failed to update addon");
      }
    } catch (e) {
      if (window.desktopProductQueue && /fetch|network/i.test(String(e?.message || ""))) {
        const taxPayload = buildTaxPayload(basePrice);
        const fields = { restaurantid: restaurantId, addonid: editingAddonId, name, price: basePrice, priceaftertax: taxPayload.priceaftertax, taxamount: taxPayload.taxamount, sgst: taxPayload.sgst, cgst: taxPayload.cgst, taxedprice: taxPayload.taxedprice };
        await enqueueProductRequest({ restaurantId, operation: "addon-save", endpoint: API_ENDPOINTS.PRODUCT_ADDONS_SAVE, fields });
        const localOnly = isFullOfflineModeEnabled();
        setAddonList((prev) => prev.map((addon) => addon.id === editingAddonId ? {
          ...addon,
          ...fields,
          __offlinePending: !localOnly,
          __localOnly: localOnly,
        } : addon));
        cancelEditAddon();
        showToast("success", localOnly
          ? "Addon saved in the Full Offline database. It will not sync online."
          : "Addon saved offline. It will sync automatically.");
      } else {
        showToast("error", e?.message || "Failed to update addon");
      }
    } finally {
      setSavingAddonEdit(false);
    }
  };

  useEffect(() => {
    if (!addonsEnabled && editingAddonId) {
      cancelEditAddon();
    }
  }, [addonsEnabled, editingAddonId]);

  useEffect(() => {
    if (!editingAddonId) return;
    const exists = addonList.some((addon) => addon.id === editingAddonId);
    if (!exists) cancelEditAddon();
  }, [addonList, editingAddonId]);

  const handleCreateAddon = async () => {
    const name = (newAddonName || "").trim();
    const basePrice = Number(newAddonPrice || 0);
    if (!name) {
      showToast("info", "Enter addon name");
      return;
    }
    if (!Number.isFinite(basePrice) || basePrice < 0) {
      showToast("info", "Enter valid addon price");
      return;
    }
    if (!restaurantId) return;

    try {
      setAddonLoading(true);
      const taxPayload = buildTaxPayload(basePrice);
      const res = await fetch(
        API_ENDPOINTS.PRODUCT_ADDONS_SAVE,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: restaurantId,
            name,
            price: basePrice,
            priceaftertax: taxPayload.priceaftertax,
            taxamount: taxPayload.taxamount,
            sgst: taxPayload.sgst,
            cgst: taxPayload.cgst,
            taxedprice: taxPayload.taxedprice,
          }),
        }
      );
      const result = await res.json();
      if (res.ok && result?.success) {
        const addon = result?.addon;
        if (addon?.id) {
          setAddonList((prev) => [...prev, addon]);
          setSelectedAddonIds((prev) => [...prev, addon.id]);
        }
        setNewAddonName("");
        setNewAddonPrice("");
        showToast("success", "Addon created");
      } else {
        showToast("error", result?.message || "Failed to create addon");
      }
    } catch (e) {
      showToast("error", e?.message || "Failed to create addon");
    } finally {
      setAddonLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const name = (newCategoryName || "").trim();
    if (!name) return showToast("info", "Enter category name");
    if (name.length > 30) return showToast("info", "Max 30 characters");
    try {
      setSavingCategory(true);
      const saveOffline = async () => {
        if (isFullOfflineModeEnabled()) {
          const result = await createOfflineCategory(restaurantId, name);
          const categories = Array.isArray(result?.categories) ? result.categories : [];
          setCategoryList(categories);
          try { localStorage.setItem("categorylistx", JSON.stringify(categories)); } catch {}
          setSelectedCategory(String(result?.category?.id || ""));
          setNewCategoryName("");
          setAddCategoryOpen(false);
          showToast("success", "Category saved permanently in Full Offline Mode");
          return true;
        }
        if (!window.desktopOfflineMutations && !window.desktopProductQueue) return false;
        const mutation = await enqueueCategoryMutation({
          restaurantId,
          operation: "category-create",
          endpoint: API_ENDPOINTS.ADD_PRODUCT_CATEGORY,
          fields: { restaurantid: restaurantId, name },
        });
        const localCategory = {
          id: mutation.localCategoryId,
          productcategoryname: name,
          is_active: 1,
          __offlinePending: true,
          __offlineMutationId: mutation.id,
        };
        const next = [localCategory, ...categoryList];
        setCategoryList(next);
        await persistCategoryCaches(restaurantId, next);
        setSelectedCategory(String(localCategory.id));
        setNewCategoryName("");
        setAddCategoryOpen(false);
        showToast("success", "Category saved offline");
        return true;
      };
      if (isDesktopOffline()) {
        await saveOffline();
        return;
      }
      const res = await fetch(
        API_ENDPOINTS.ADD_PRODUCT_CATEGORY,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId, name }),
        }
      );
      const result = await res.json();
      if (res.ok && (result.success || result.status === true)) {
        showToast("success", "Category added");
        setNewCategoryName("");
        setAddCategoryOpen(false);
        await fetchCategories();
      } else {
        const msg =
          (result?.errors &&
            Object.values(result.errors).flat().join("\n")) ||
          result?.message ||
          "Failed to add category";
        showToast("error", msg);
      }
    } catch (e) {
      const queued = /fetch|network/i.test(String(e?.message || ""))
        ? await (async () => {
            if (!window.desktopProductQueue) return false;
            const mutation = await enqueueCategoryMutation({
              restaurantId,
              operation: "category-create",
              endpoint: API_ENDPOINTS.ADD_PRODUCT_CATEGORY,
              fields: { restaurantid: restaurantId, name },
            });
            const localOnly = isFullOfflineModeEnabled();
            const localCategory = {
              id: mutation.localCategoryId,
              productcategoryname: name,
              is_active: 1,
              __offlinePending: !localOnly,
              __localOnly: localOnly,
              __offlineMutationId: mutation.id,
            };
            const next = [localCategory, ...categoryList];
            setCategoryList(next);
            await persistCategoryCaches(restaurantId, next);
            setSelectedCategory(String(localCategory.id));
            setNewCategoryName("");
            setAddCategoryOpen(false);
            showToast("success", "Category saved offline");
            return true;
          })()
        : false;
      if (!queued) showToast("error", e?.message || "Failed to add category");
    } finally {
      setSavingCategory(false);
    }
  };

  const fetchVariantsForProduct = async (productId) => {
    if (!restaurantId || !productId) return;
    if (isFullOfflineModeEnabled()) return;
    try {
      const res = await fetch(
        API_ENDPOINTS.PRODUCT_VARIANTS_LIST,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: restaurantId,
            productid: productId,
          }),
        }
      );
      const result = await res.json();
      if (res.ok && result?.success) {
        const list = Array.isArray(result?.variants) ? result.variants : [];
        const mapped = list.map((variant) => ({
          id: variant.id,
          variantid: variant.id,
          label: variant.label || "",
          price: String(variant.price ?? ""),
          is_default: variant.is_default,
          displayorder: Number(variant.displayorder ?? 0),
        }));
        setVariants(mapped);
        setLoadedVariantsSnapshot(mapped);
        const defaultVariant =
          mapped.find((variant) => Number(variant?.is_default) === 1) ||
          mapped[0];
        setDefaultVariantId(defaultVariant?.id ?? null);
        setVariantsEnabled(mapped.length > 0);
      } else {
        setVariants([]);
        setLoadedVariantsSnapshot([]);
        setVariantsEnabled(false);
        setDefaultVariantId(null);
      }
    } catch (e) {
      setVariants([]);
      setLoadedVariantsSnapshot([]);
      setVariantsEnabled(false);
      setDefaultVariantId(null);
    }
  };

  const fetchAssignedAddons = async (productId) => {
    if (!restaurantId || !productId) return;
    if (isFullOfflineModeEnabled()) return;
    try {
      const res = await fetch(
        API_ENDPOINTS.PRODUCT_ADDONS_BY_PRODUCT,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: restaurantId,
            productid: productId,
          }),
        }
      );
      const result = await res.json();
      if (res.ok && result?.success) {
        const addons = Array.isArray(result?.addons) ? result.addons : [];
        setSelectedAddonIds(addons.map((addon) => addon.id));
        setAddonsEnabled(addons.length > 0);
      } else {
        setSelectedAddonIds([]);
        setAddonsEnabled(false);
      }
    } catch (e) {
      setSelectedAddonIds([]);
      setAddonsEnabled(false);
    }
  };

  useEffect(() => {
    if (!restaurantId || !editingProductId) return;
    if (isFullOfflineModeEnabled()) return;
    fetchVariantsForProduct(editingProductId);
    fetchAssignedAddons(editingProductId);
    fetchAddonList(restaurantId);
  }, [restaurantId, editingProductId]);

  // === GST math: same as tumhara original ===
  // taxType: "withouttax" => price is base; "withtax" => price is tax-inclusive
  const calculateAmount = (basePrice, gstPer, choice) => {
    const p = Number(basePrice || 0);
    const rate = Number(gstPer || 0) / 100;

    if (!rate || !p || !choice) {
      return {
        priceaftertax: p.toFixed(2),
        taxAmount: 0,
        sgst: 0,
        cgst: 0,
        apiChoice: choice === "withtax" ? "withgst" : "withoutgst",
      };
    }

    if (choice === "withtax") {
      const base = +(p / (1 + rate)).toFixed(2);
      const gstAmount = +(p - base).toFixed(2);
      return {
        priceaftertax: base.toFixed(2),
        taxAmount: gstAmount,
        sgst: genericTax ? 0 : +(gstAmount / 2).toFixed(2),
        cgst: genericTax ? 0 : +(gstAmount / 2).toFixed(2),
        apiChoice: "withgst",
      };
    }

    if (choice === "withouttax") {
      const gstAmount = +(p * rate).toFixed(2);
      return {
        priceaftertax: p.toFixed(2),
        taxAmount: gstAmount,
        sgst: genericTax ? 0 : +(gstAmount / 2).toFixed(2),
        cgst: genericTax ? 0 : +(gstAmount / 2).toFixed(2),
        apiChoice: "withoutgst",
      };
    }

    return {
      priceaftertax: "0.00",
      taxAmount: 0,
      sgst: 0,
      cgst: 0,
      apiChoice: "withoutgst",
    };
  };

  const buildTaxPayload = (basePrice) => {
    const calc = calculateAmount(
      Number(basePrice || 0),
      gstPercent,
      gstEnabled ? taxChoice : ""
    );
    const base = Number(basePrice || 0);
    const taxedprice = gstEnabled
      ? taxChoice === "withtax"
        ? base
        : Number(calc.priceaftertax) + Number(calc.taxAmount || 0)
      : base;
    return {
      priceaftertax: Number(calc.priceaftertax || base).toFixed(2),
      taxamount: Number(calc.taxAmount || 0).toFixed(2),
      sgst: Number(calc.sgst || 0).toFixed(2),
      cgst: Number(calc.cgst || 0).toFixed(2),
      taxedprice: Number(taxedprice || 0).toFixed(2),
      apiChoice: calc.apiChoice,
    };
  };

  const preview = useMemo(() => {
    if (!price || !gstEnabled || !gstPercent || !taxChoice) return null;
    return calculateAmount(Number(price), gstPercent, taxChoice);
  }, [price, gstEnabled, gstPercent, taxChoice]);

  const resetForm = () => {
    setProductName("");
    setPrice("");
    setPriceTouched(false);
    setSelectedCategory("");
    setGstEnabled(false);
    setGstPercent("");
    setTaxChoice("");
    setEditingProductId(null);
    setProductImageFile(null);
    setImagePreview(null);
    setDisplayOrder("0");
    setInventoryLinkEnabled(false);
    setSelectedInventoryItemId("");
    setVariantsEnabled(false);
    setVariants([]);
    setDefaultVariantId(null);
    setLoadedVariantsSnapshot([]);
    setAddonsEnabled(false);
    setSelectedAddonIds([]);
    setNewAddonName("");
    setNewAddonPrice("");
    setEditingAddonId(null);
    setEditAddonName("");
    setEditAddonPrice("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProductImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const generateAiImage = async () => {
    const prompt = window.prompt("Describe the product image", `Professional restaurant menu photo of ${productName || "food product"}`);
    if (!prompt?.trim()) return;
    setGeneratingImage(true);
    try {
      let response = await fetch(API_ENDPOINTS.AI_GENERATE_IMAGE, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt: prompt.trim() }),
      });
      if (!response.ok) response = await fetch(API_ENDPOINTS.AI_GENERATE_IMAGE_FALLBACK, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prompt: prompt.trim() }),
      });
      const result = await response.json().catch(() => ({}));
      const generated = result?.images?.[0];
      if (!response.ok || !result?.success || !generated?.base64) throw new Error(result?.message || "AI image generation failed");
      const mime = generated.mime_type || "image/png";
      const bytes = Uint8Array.from(atob(generated.base64), (char) => char.charCodeAt(0));
      const file = new File([bytes], `product_ai_${Date.now()}.png`, { type: mime });
      setProductImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      showToast("success", "AI product image generated");
    } catch (error) { showToast("error", error?.message || "AI image generation failed"); }
    finally { setGeneratingImage(false); }
  };

  const saveVariantsForProduct = async (productId) => {
    const sanitized = variants
      .map((variant, index) => ({
        ...variant,
        variantid: getPersistedVariantId(variant),
        id: getPersistedVariantId(variant) ?? variant.id,
        label: (variant.label || "").trim(),
        price: Number(variant.price || 0),
        displayorder: index,
      }))
      .filter(
        (variant) =>
          variantsEnabled && variant.label && Number.isFinite(variant.price)
      );

    const saveVariant = async (variant, { isDefault = 0, isActive = 1 } = {}) => {
      const taxPayload = buildTaxPayload(variant.price);
      const persistedId = getPersistedVariantId(variant);
      try {
        const res = await fetch(
          API_ENDPOINTS.PRODUCT_VARIANTS_SAVE,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              restaurantid: restaurantId,
              productid: productId,
              ...(persistedId ? { variantid: persistedId } : {}),
              label: variant.label,
              price: variant.price,
              priceaftertax: taxPayload.priceaftertax,
              taxamount: taxPayload.taxamount,
              sgst: taxPayload.sgst,
              cgst: taxPayload.cgst,
              taxedprice: taxPayload.taxedprice,
              is_default: isDefault,
              displayorder: variant.displayorder ?? 0,
              is_active: isActive,
            }),
          }
        );
        const result = await res.json();
        return res.ok && result?.success;
      } catch {
        return false;
      }
    };

    const persistedActiveIds = new Set(
      sanitized
        .map((variant) => getPersistedVariantId(variant))
        .filter((id) => Number.isFinite(id))
    );
    const staleVariants = loadedVariantsSnapshot
      .map((variant, index) => ({
        ...variant,
        label: (variant.label || "").trim(),
        price: Number(variant.price || 0),
        displayorder: Number(variant.displayorder ?? index),
      }))
      .filter((variant) => {
        const persistedId = getPersistedVariantId(variant);
        return persistedId && !persistedActiveIds.has(persistedId);
      });

    if (!sanitized.length && !staleVariants.length) {
      return { ok: true, skipped: true };
    }

    const sanitizedIds = new Set(sanitized.map((variant) => variant.id));
    const defaultId = sanitizedIds.has(defaultVariantId)
      ? defaultVariantId
      : sanitized[0]?.id;
    const responses = await Promise.all(
      sanitized.map((variant) =>
        saveVariant(variant, {
          isDefault: variant.id === defaultId ? 1 : 0,
          isActive: 1,
        })
      )
    );
    const staleResponses = await Promise.all(
      staleVariants.map((variant) =>
        saveVariant(variant, {
          isDefault: 0,
          isActive: 0,
        })
      )
    );

    return {
      ok: responses.every(Boolean) && staleResponses.every(Boolean),
      skipped: false,
    };
  };

  const assignAddonsForProduct = async (productId) => {
    if (!addonsEnabled || !selectedAddonIds.length) {
      return { ok: true, skipped: true };
    }
    try {
      const res = await fetch(
        API_ENDPOINTS.PRODUCT_ADDONS_ASSIGN,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: restaurantId,
            productid: productId,
            addon_ids: selectedAddonIds,
            replace: true,
          }),
        }
      );
      const result = await res.json();
      return { ok: res.ok && result?.success, skipped: false };
    } catch {
      return { ok: false, skipped: false };
    }
  };

  const queueExistingProductOptions = async (productId) => {
    const sanitized = variants
      .map((variant, index) => ({
        ...variant,
        variantid: getPersistedVariantId(variant),
        label: (variant.label || "").trim(),
        price: Number(variant.price || 0),
        displayorder: index,
      }))
      .filter((variant) => variantsEnabled && variant.label && Number.isFinite(variant.price));
    const activeIds = new Set(sanitized.map((variant) => variant.variantid).filter(Boolean));
    const stale = loadedVariantsSnapshot.filter((variant) => {
      const id = getPersistedVariantId(variant);
      return id && !activeIds.has(id);
    });
    const defaultId = sanitized.some((variant) => variant.id === defaultVariantId)
      ? defaultVariantId
      : sanitized[0]?.id;
    const queueVariant = async (variant, isActive) => {
      const taxPayload = buildTaxPayload(variant.price);
      await enqueueProductRequest({
        restaurantId,
        operation: "variant-save",
        endpoint: API_ENDPOINTS.PRODUCT_VARIANTS_SAVE,
        fields: {
          restaurantid: restaurantId, productid: productId,
          ...(getPersistedVariantId(variant) ? { variantid: getPersistedVariantId(variant) } : {}),
          label: variant.label, price: variant.price,
          priceaftertax: taxPayload.priceaftertax, taxamount: taxPayload.taxamount,
          sgst: taxPayload.sgst, cgst: taxPayload.cgst, taxedprice: taxPayload.taxedprice,
          is_default: isActive && variant.id === defaultId ? 1 : 0,
          displayorder: variant.displayorder || 0, is_active: isActive ? 1 : 0,
        },
      });
    };
    await Promise.all([
      ...sanitized.map((variant) => queueVariant(variant, true)),
      ...stale.map((variant) => queueVariant(variant, false)),
    ]);
    await enqueueProductRequest({
      restaurantId,
      operation: "addon-assign",
      endpoint: API_ENDPOINTS.PRODUCT_ADDONS_ASSIGN,
      fields: { restaurantid: restaurantId, productid: productId, addon_ids: selectedAddonIds, replace: true },
    });
  };

  const handleSubmit = async () => {
    if (!selectedCategory) {
      showToast("info", "Select category");
      return;
    }
    if (!productName.trim()) {
      showToast("info", "Enter product name");
      return;
    }
    const validVariants = variantsEnabled
      ? variants.filter(
          (variant) =>
            (variant.label || "").trim() &&
            String(variant.price || "").trim()
        )
      : [];
    if (variantsEnabled && !validVariants.length) {
      showToast("info", "Enter variant details");
      return;
    }
    const defaultVariant = variantsEnabled
      ? validVariants.find((variant) => variant.id === defaultVariantId) ||
        validVariants[0]
      : null;
    const basePriceValue = variantsEnabled ? defaultVariant?.price : price;
    if (!basePriceValue) {
      showToast("info", "Enter price");
      return;
    }
    if (gstEnabled && (!gstPercent || !taxChoice)) {
      showToast("info", `Enter ${taxDisplayLabel} % and select tax type`);
      return;
    }

    const normalizedOrder =
      displayOrder?.trim() === ""
        ? 0
        : Number(displayOrder?.trim().replace(/[^0-9]/g, ""));
    if (Number.isNaN(normalizedOrder)) {
      showToast("info", "Invalid display order");
      return;
    }

    const gstData = calculateAmount(
      Number(basePriceValue || 0),
      gstPercent,
      gstEnabled ? taxChoice : ""
    );

    const form = new FormData();
    form.append("restaurantid", restaurantId);
    form.append("productcategoryid", selectedCategory);
    form.append("name", productName.trim());
    form.append("price", basePriceValue);
    form.append("gstenable", gstEnabled ? 1 : 0);
    form.append("gstpercetage", gstEnabled ? gstPercent : 0); // typo intentional
    form.append(
      "withorwithoutgst",
      gstEnabled ? gstData.apiChoice : "withoutgst"
    );
    form.append("priceaftertax", gstData.priceaftertax);
    form.append("taxamount", gstData.taxAmount);
    form.append("sgst", gstData.sgst);
    form.append("cgst", gstData.cgst);
    form.append("size", "Full");
    form.append("displayorder", String(normalizedOrder));
    form.append("status", statusValue);
    form.append(
      "inventory_item_id",
      inventoryLinkEnabled && selectedInventoryItemId
        ? String(selectedInventoryItemId)
        : ""
    );

    if (editingProductId) {
      form.append("productid", editingProductId);
      form.append("id", editingProductId);
    }

    if (productImageFile) {
      form.append("photo", productImageFile);
    }

    const url = editingProductId
      ? API_ENDPOINTS.EDIT_PRODUCT
      : API_ENDPOINTS.ADD_PRODUCT;

    const fields = {
      restaurantid: restaurantId,
      productcategoryid: selectedCategory,
      name: productName.trim(),
      price: basePriceValue,
      gstenable: gstEnabled ? 1 : 0,
      gstpercetage: gstEnabled ? gstPercent : 0,
      withorwithoutgst: gstEnabled ? gstData.apiChoice : "withoutgst",
      priceaftertax: gstData.priceaftertax,
      taxamount: gstData.taxAmount,
      sgst: gstData.sgst,
      cgst: gstData.cgst,
      size: "Full",
      displayorder: String(normalizedOrder),
      status: statusValue,
      inventory_item_id: inventoryLinkEnabled && selectedInventoryItemId ? String(selectedInventoryItemId) : "",
      ...(editingProductId ? { productid: editingProductId, id: editingProductId } : {}),
    };

    const saveOffline = async () => {
      const operation = editingProductId ? "update" : "create";
      const offlineImage = productImageFile ? await readOfflineFile(productImageFile) : null;
      const queuedFields = offlineImage ? { ...fields, photo: offlineImage } : fields;
      if (isFullOfflineModeEnabled()) {
        if (!hasOfflineProductStore()) {
          throw new Error("Restart the desktop app to initialize offline product storage.");
        }
        const productRecord = {
          ...queuedFields,
          ...(offlineImage ? { photo: offlineImage.dataUrl } : {}),
          __offlineVariants: variantsEnabled ? validVariants : [],
          __offlineDefaultVariantId: variantsEnabled ? defaultVariantId : null,
          __offlineAddonIds: addonsEnabled ? selectedAddonIds : [],
        };
        const result = editingProductId
          ? await updateOfflineProduct(restaurantId, editingProductId, productRecord)
          : await createOfflineProduct(restaurantId, productRecord);
        applyOfflineProducts(result?.products);
        const savedProduct = result?.product;
        resetForm();
        showToast("success", "Product saved permanently in Full Offline Mode");
        if (typeof onSaved === "function") {
          await onSaved({
            id: savedProduct?.id,
            name: savedProduct?.name,
            categoryId: savedProduct?.productcategoryid,
          });
        }
        return true;
      }
      if (!window.desktopOfflineMutations && !window.desktopProductQueue) return false;
      const mutationId = await enqueueProductMutation({ restaurantId, operation, fields: queuedFields });
      const existing = (productList || []).find((item) => String(getProductId(item) || item?.id) === String(editingProductId));
      const localProductId = editingProductId || `offline-${mutationId}`;
      const localProduct = {
        ...(existing || {}),
        ...fields,
        ...(offlineImage ? { photo: offlineImage.dataUrl } : {}),
        id: localProductId,
        productid: localProductId,
        __offlinePending: true,
        __offlineMutationId: mutationId,
      };
      const next = editingProductId
        ? (productList || []).map((item) => String(getProductId(item) || item?.id) === String(editingProductId) ? localProduct : item)
        : [localProduct, ...(productList || [])];
      await saveProductListLocally(next);
      if (editingProductId || variantsEnabled || addonsEnabled) {
        await queueExistingProductOptions(localProductId);
      }
      resetForm();
      showToast(
        "success",
        "Product saved offline. It will sync automatically when internet returns."
      );
      return true;
    };

    try {
      setSubmitting(true);
      if (isDesktopOffline()) {
        await saveOffline();
        return;
      }
      const res = await fetch(url, { method: "POST", body: form });
      const result = await res.json();
      if (res.ok && (result.success || result.status === true)) {
        const productId =
          editingProductId || result?.id || result?.product_id;
        let optionsFailed = false;
        if (productId && (variantsEnabled || addonsEnabled)) {
          setSavingOptions(true);
          const [variantResult, addonResult] = await Promise.all([
            saveVariantsForProduct(productId),
            assignAddonsForProduct(productId),
          ]);
          optionsFailed = !variantResult.ok || !addonResult.ok;
          setSavingOptions(false);
        }
        showToast(
          "success",
          optionsFailed
            ? "Product saved (options partial)"
            : editingProductId
            ? "Product updated"
            : "Product added"
        );
        resetForm();
        await fetchProducts();
        if (typeof onSaved === "function") {
          await onSaved({
            id: productId,
            name: productName.trim(),
            categoryId: selectedCategory,
          });
        }
      } else {
        showToast("error", result?.message || "Error saving product");
      }
    } catch (e) {
      const queued = window.desktopProductQueue && /fetch|network/i.test(String(e?.message || ""))
        ? await saveOffline()
        : false;
      if (!queued) showToast("error", e?.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  const editProduct = (item) => {
    setProductName(item.name || "");
    setPrice(item.price || "");
    setPriceTouched(false);
    setSelectedCategory(String(item.productcategoryid || ""));
    setGstEnabled(item.gstenable === 1 || item.gstenable === "1");
    setGstPercent(String(item.gstpercetage || ""));
    const taxFlag = String(item.withorwithoutgst || "").toLowerCase();
    if (taxFlag === "withgst" || taxFlag === "withtax") {
      setTaxChoice("withtax");
    } else if (taxFlag === "withoutgst" || taxFlag === "withouttax") {
      setTaxChoice("withouttax");
    } else {
      setTaxChoice("");
    }
    setEditingProductId(item.id || item.productid || item.product_id || null);
    setStatusValue(
      item.status === 0 || item.status === "0" ? "0" : "1"
    );
    setDisplayOrder(String(item.displayorder ?? item.display_order ?? 0));
    setInventoryLinkEnabled(Boolean(item?.inventory_item_id));
    setSelectedInventoryItemId(
      item?.inventory_item_id ? String(item.inventory_item_id) : ""
    );
    setProductImageFile(null);
    setImagePreview(null);
    const offlineVariants = Array.isArray(item?.__offlineVariants)
      ? item.__offlineVariants
      : [];
    setVariantsEnabled(isFullOfflineModeEnabled() && offlineVariants.length > 0);
    setVariants(offlineVariants);
    setDefaultVariantId(item?.__offlineDefaultVariantId || offlineVariants[0]?.id || null);
    setLoadedVariantsSnapshot([]);
    setEditingAddonId(null);
    setEditAddonName("");
    setEditAddonPrice("");
    const offlineAddonIds = Array.isArray(item?.__offlineAddonIds)
      ? item.__offlineAddonIds
      : [];
    setAddonsEnabled(isFullOfflineModeEnabled() && offlineAddonIds.length > 0);
    setSelectedAddonIds(offlineAddonIds);
    setNewAddonName("");
    setNewAddonPrice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ===== list filtering + pagination =====
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (productList || []).filter((p) => {
      const catMatch = catFilter
        ? String(p.productcategoryid) === String(catFilter)
        : true;
      const text = `${p.name || ""} ${p.price || ""} ${
        p.withorwithoutgst || ""
      }`.toLowerCase();
      return catMatch && (q ? text.includes(q) : true);
    });
  }, [productList, search, catFilter]);

  const totalPages = Math.ceil(filtered.length / productsPerPage) || 1;
  const selectedProductIdSet = useMemo(
    () => new Set((selectedProductIds || []).map(String).filter(Boolean)),
    [selectedProductIds]
  );
  const filteredProductIds = useMemo(
    () =>
      filtered
        .map(getProductKey)
        .filter(Boolean),
    [filtered]
  );
  const allFilteredSelected =
    filteredProductIds.length > 0 &&
    filteredProductIds.every((id) => selectedProductIdSet.has(id));
  const paginatedProducts = filtered.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  useEffect(() => {
    const availableIds = new Set(
      (productList || [])
        .map(getProductKey)
        .filter(Boolean)
    );
    setSelectedProductIds((prev) =>
      (prev || []).map(String).filter((id) => availableIds.has(id))
    );
  }, [productList]);

  const toggleSelectProduct = (productId) => {
    const id = String(productId || "").trim();
    if (!id) return;
    setSelectedProductIds((prev) => {
      const next = new Set((prev || []).map(String));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return Array.from(next);
    });
  };

  const toggleSelectAllFiltered = () => {
    if (!filteredProductIds.length) return;
    if (allFilteredSelected) {
      const filteredSet = new Set(filteredProductIds);
      setSelectedProductIds((prev) =>
        (prev || []).filter((id) => !filteredSet.has(String(id)))
      );
      return;
    }
    setSelectedProductIds((prev) =>
      Array.from(new Set([...(prev || []).map(String), ...filteredProductIds]))
    );
  };


  const deleteSingleProduct = async (
    product,
    { silent = false, forceDelete = false } = {}
  ) => {
    const fullOfflineEnabled = await getFullOfflineModeEnabled();
    const rawId = product?.id ?? product?.productid ?? product?.product_id;
    const id = fullOfflineEnabled ? String(rawId || "").trim() : getProductId(product);
    if (!id || (!fullOfflineEnabled && !Number.isFinite(id))) {
      if (!silent) showToast("error", "Invalid product id");
      return { ok: false };
    }
    const fields = {
      restaurantid: restaurantId,
      productid: id,
      ...(forceDelete ? { force_delete: true } : {}),
    };
    if (fullOfflineEnabled) {
      if (!hasOfflineProductStore()) {
        throw new Error("Restart the desktop app to initialize offline product storage.");
      }
      const result = await deleteOfflineProduct(restaurantId, id);
      return {
        ok: true,
        localOnly: true,
        products: result?.products,
        message: "Product deleted from the Full Offline database",
      };
    }
    if (isDesktopOffline()) {
      await enqueueProductMutation({ restaurantId, operation: "delete", fields });
      return {
        ok: true,
        queued: true,
        message: "Product deleted offline; pending sync",
      };
    }
    let res;
    try {
      res = await fetch(API_ENDPOINTS.DELETE_PRODUCT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });
    } catch (error) {
      if (window.desktopProductQueue && /fetch|network/i.test(String(error?.message || ""))) {
        await enqueueProductMutation({ restaurantId, operation: "delete", fields });
        return { ok: true, queued: true, message: "Product deleted offline; pending sync" };
      }
      throw error;
    }
    let payload = {};
    try {
      payload = await res.json();
    } catch {
      payload = {};
    }
    const ok = res.ok && payload?.success !== false;
    const message = payload?.message || payload?.errors || "";
    const requiresConfirmation = payload?.requires_confirmation === true;
    if (!ok && !silent && !requiresConfirmation) {
      showToast("error", message || "Unable to delete product");
    }
    return {
      ok,
      requiresConfirmation,
      message: String(message || "").trim(),
    };
  };

  const applyDeletedProduct = async (product, result) => {
    const id = product?.id ?? product?.productid ?? product?.product_id;
    if (editingProductId && String(editingProductId) === String(id)) {
      resetForm();
    }
    const remaining = Array.isArray(result?.products)
      ? result.products
      : (productList || []).filter((item) => getProductKey(item) !== String(id));
    setProductList(remaining);
    if (result?.localOnly) {
      try { localStorage.setItem("productlistx", JSON.stringify(remaining)); } catch {}
    } else {
      await persistProductCaches(restaurantId, remaining);
    }
    setSelectedProductIds((prev) =>
      (prev || []).filter((selectedId) => String(selectedId) !== String(id))
    );
    setUsedProductDeleteTarget(null);
    setUsedProductDeleteAgreed(false);
    showToast("success", result.message || "Product deleted");
  };

  const handleDeleteProduct = async (product) => {
    const id = getProductKey(product);
    if (!id) return;
    const confirmed = window.confirm(
      `Delete product "${product?.name || "this product"}"?`
    );
    if (!confirmed) return;
    try {
      setDeletingProductId(id);
      const result = await deleteSingleProduct(product);
      if (result.requiresConfirmation) {
        setUsedProductDeleteTarget(product);
        setUsedProductDeleteAgreed(false);
        return;
      }
      if (!result.ok) return;
      await applyDeletedProduct(product, result);
    } catch (e) {
      showToast("error", e?.message || "Unable to delete product");
    } finally {
      setDeletingProductId(null);
    }
  };

  const confirmUsedProductDelete = async () => {
    const products = getUsedDeleteTargets(usedProductDeleteTarget);
    const isBulkConfirmation = usedProductDeleteTarget?.__bulk === true;
    const alreadyDeletedCount = isBulkConfirmation
      ? Number(usedProductDeleteTarget?.alreadyDeletedCount || 0)
      : 0;
    if (!usedProductDeleteAgreed || products.length === 0) return;

    try {
      if (isBulkConfirmation) setBulkDeleting(true);
      else setDeletingProductId(getProductKey(products[0]));

      const results = await mapWithConcurrency(
        products,
        (product) => deleteSingleProduct(product, { forceDelete: true, silent: true }),
        5
      );
      const deletedProducts = products.filter((_, index) => results[index]?.ok);
      const deletedIds = new Set(deletedProducts.map(getProductKey));
      const failedMessages = results
        .filter((result) => !result?.ok && result?.message)
        .map((result) => result.message);

      if (deletedIds.size > 0) {
        const remaining = (productList || []).filter(
          (item) => !deletedIds.has(getProductKey(item))
        );
        if (editingProductId && deletedIds.has(String(editingProductId))) resetForm();
        setProductList(remaining);
        if (isFullOfflineModeEnabled()) {
          try { localStorage.setItem("productlistx", JSON.stringify(remaining)); } catch {}
        } else {
          await persistProductCaches(restaurantId, remaining);
        }
        setSelectedProductIds((prev) =>
          (prev || []).filter((id) => !deletedIds.has(String(id)))
        );
      }

      setUsedProductDeleteTarget(null);
      setUsedProductDeleteAgreed(false);
      const failedCount = products.length - deletedIds.size;
      const totalDeletedCount = alreadyDeletedCount + deletedIds.size;
      if (totalDeletedCount && failedCount === 0) {
        showToast("success", `${totalDeletedCount} product${totalDeletedCount > 1 ? "s" : ""} deleted`);
      } else if (totalDeletedCount) {
        showToast("info", `${totalDeletedCount} deleted, ${failedCount} failed`);
      } else {
        showToast("error", failedMessages[0] || "Unable to delete selected products");
      }
    } catch (error) {
      showToast("error", error?.message || "Unable to delete product");
    } finally {
      setDeletingProductId(null);
      setBulkDeleting(false);
    }
  };

  const handleDeleteSelectedProducts = async () => {
    if (!selectedProductIds.length) {
      showToast("info", "Select products to delete");
      return;
    }
    const total = selectedProductIds.length;
    const confirmed = window.confirm(
      `Delete ${total} selected product${total > 1 ? "s" : ""}?`
    );
    if (!confirmed) return;
    try {
      setBulkDeleting(true);
      const selectedSet = new Set(selectedProductIds.map(String));
      const selectedProducts = dedupeProductsById(productList).filter((item) =>
        selectedSet.has(getProductKey(item))
      );
      const results = await mapWithConcurrency(
        selectedProducts,
        (item) => deleteSingleProduct(item, { silent: true }),
        5
      );
      const successIds = selectedProducts
        .filter((_, idx) => results[idx]?.ok)
        .map(getProductKey);
      const successIdSet = new Set(successIds);
      const confirmationProducts = selectedProducts.filter(
        (_, index) => results[index]?.requiresConfirmation
      );
      const failedCount = results.filter(
        (result) => !result?.ok && !result?.requiresConfirmation
      ).length;
      const failedMessages = results
        .filter(
          (result) => !result?.ok && !result?.requiresConfirmation && result?.message
        )
        .map((result) => result.message);
      if (successIds.length) {
        setProductList((prev) =>
          (prev || []).filter((item) => !successIdSet.has(getProductKey(item)))
        );
        const remaining = (productList || []).filter(
          (item) => !successIdSet.has(getProductKey(item))
        );
        if (isFullOfflineModeEnabled()) {
          try { localStorage.setItem("productlistx", JSON.stringify(remaining)); } catch {}
        } else {
          await persistProductCaches(restaurantId, remaining);
        }
        setSelectedProductIds((prev) =>
          (prev || []).filter((id) => !successIdSet.has(String(id)))
        );
      }
      if (confirmationProducts.length) {
        setUsedProductDeleteTarget({
          __bulk: true,
          products: confirmationProducts,
          selectedCount: total,
          alreadyDeletedCount: successIds.length,
        });
        setUsedProductDeleteAgreed(false);
        if (failedCount > 0) {
          showToast("info", `${failedCount} product${failedCount > 1 ? "s" : ""} could not be deleted`);
        }
      } else if (successIds.length && failedCount === 0) {
        showToast("success", `${successIds.length} product deleted`);
      } else if (successIds.length && failedCount > 0) {
        const reason = failedMessages[0] ? ` (${failedMessages[0]})` : "";
        showToast("info", `${successIds.length} deleted, ${failedCount} failed${reason}`);
      } else {
        showToast(
          "error",
          failedMessages[0] || "Failed to delete selected products"
        );
      }
    } catch (e) {
      showToast("error", e?.message || "Unable to delete selected products");
    } finally {
      setBulkDeleting(false);
    }
  };

  const usedDeleteProducts = getUsedDeleteTargets(usedProductDeleteTarget);
  const usedDeleteIsBulk = usedProductDeleteTarget?.__bulk === true;
  const usedDeleteSelectedCount = usedDeleteIsBulk
    ? Number(usedProductDeleteTarget?.selectedCount || usedDeleteProducts.length)
    : 1;
  const usedDeleteBusy = usedDeleteIsBulk
    ? bulkDeleting
    : deletingProductId !== null &&
      String(deletingProductId) === getProductKey(usedDeleteProducts[0]);

  return (
    <div style={embedded ? sx.embeddedPage : sx.page}>
      {/* Header */}
      {!embedded ? <div style={sx.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={sx.logoBadge}>{currencySymbol}</div>
          <div>
            <div style={sx.title}>
              {editingProductId ? "Edit Product" : "Manage Products"}
            </div>
            <div style={sx.subtitle}>Create and manage your menu items</div>
          </div>
        </div>
        <div style={sx.headerRight}>
          <div style={sx.pill}>
            <span style={sx.pillDot} /> Total: <b>{productList.length}</b>
          </div>
        </div>
      </div> : null}

      {/* Form Card */}
      <div style={embedded ? sx.embeddedCard : sx.card}>
        <div style={sx.formLayout} className="add-product-form-layout">
          <div style={sx.leftCol}>
            <div style={sx.formGrid}>
              <div style={sx.fieldBlock}>
                <label style={sx.label}>Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  style={sx.select}
                >
                  <option value="">Select Category</option>
                  {(categoryList || []).map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.productcategoryname}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setAddCategoryOpen(true)}
                  style={sx.linkBtn}
                >
                  + Add new category
                </button>
              </div>

              <div style={sx.fieldBlock}>
                <label style={sx.label}>Product Name</label>
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="e.g., Paneer Tikka"
                  style={sx.input}
                  maxLength={40}
                />
                <div style={sx.mutedRight}>{productName.length}/40</div>
              </div>
            </div>

            <div style={sx.inlineRow}>
              <div style={{ ...sx.fieldBlock, flex: 1, minWidth: 140 }}>
                <label style={sx.label}>Price ({currencySymbol})</label>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9.]*"
                  value={price}
                  onChange={(e) => {
                    setPriceTouched(true);
                    setPrice(sanitizePriceInput(e.target.value));
                  }}
                  placeholder="e.g., 199"
                  style={sx.input}
                  maxLength={PRICE_INPUT_MAX_CHARS}
                />
              </div>
              <div style={{ ...sx.fieldBlock, flex: 1, minWidth: 140 }}>
                <label style={sx.label}>Display Order</label>
                <input
                  type="text"
                  value={displayOrder}
                  onChange={(e) =>
                    setDisplayOrder(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  placeholder="0"
                  style={sx.input}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={3}
                />
              </div>
              <div style={{ ...sx.fieldBlock, flex: 1, minWidth: 140 }}>
                <label style={sx.label}>Status</label>
                <select
                  value={statusValue}
                  onChange={(e) => setStatusValue(e.target.value)}
                  style={sx.select}
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
              <div style={{ ...sx.fieldBlock, flex: 1, minWidth: 140 }}>
                <label style={sx.label}>Enable {taxDisplayLabel}</label>
                <div style={sx.switchRow}>
                  <input
                    id="gst-toggle"
                    type="checkbox"
                    checked={gstEnabled}
                    onChange={() => {
                      const val = !gstEnabled;
                      setGstEnabled(val);
                      if (!val) {
                        setGstPercent("");
                        setTaxChoice("");
                      }
                    }}
                  />
                  <label htmlFor="gst-toggle" style={{ marginLeft: 8 }}>
                    {gstEnabled ? "On" : "Off"}
                  </label>
                </div>
              </div>
            </div>

            {gstEnabled && (
              <div style={sx.inlineRow}>
                <div style={{ ...sx.fieldBlock, flex: 1, minWidth: 160 }}>
                  <label style={sx.label}>{taxDisplayLabel} %</label>
                  {genericTax ? (
                    <input
                      value={gstPercent}
                      onChange={(e) => setGstPercent(e.target.value.replace(/[^0-9.]/g, ""))}
                      style={sx.input}
                      placeholder={`Enter ${taxDisplayLabel} %`}
                      inputMode="decimal"
                      maxLength={6}
                    />
                  ) : (
                    <select
                      value={gstPercent}
                      onChange={(e) => setGstPercent(e.target.value)}
                      style={sx.select}
                    >
                      <option value="">Select %</option>
                      <option value="5">5%</option>
                      <option value="18">18%</option>
                    </select>
                  )}
                </div>

                <div style={{ ...sx.fieldBlock, flex: 2, minWidth: 220 }}>
                  <label style={sx.label}>Tax Type</label>
                  <div style={sx.pillsRow}>
                    <button
                      type="button"
                      onClick={() => setTaxChoice("withtax")}
                      style={{
                        ...sx.pillBtn,
                        ...(taxChoice === "withtax" ? sx.pillBtnActive : {}),
                      }}
                    >
                      With Tax
                    </button>
                    <button
                      type="button"
                      onClick={() => setTaxChoice("withouttax")}
                      style={{
                        ...sx.pillBtn,
                        ...(taxChoice === "withouttax" ? sx.pillBtnActive : {}),
                      }}
                    >
                      Without Tax
                    </button>
                  </div>
                </div>
              </div>
            )}

            {preview && (
              <div style={sx.previewBox}>
                <div style={sx.previewLine}>
                  Price after tax base: <b>{fmtMoney(preview.priceaftertax)}</b>
                </div>
                <div style={sx.previewLine}>
                  {taxDisplayLabel}: {fmtMoney(preview.taxAmount)}
                  {!genericTax ? ` (SGST ${fmtMoney(preview.sgst)} / CGST ${fmtMoney(preview.cgst)})` : ""}
                </div>
                <div style={sx.previewHint}>
                  Flag: <code>{preview.apiChoice}</code>
                </div>
              </div>
            )}

            <div style={sx.fieldBlock}>
              <label style={sx.label}>Product Image (optional)</label>
              <label style={sx.uploadBox}>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleImageChange}
                  />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>
                    {productImageFile ? "Change image" : "Upload image"}
                  </div>
                  <div style={sx.mutedSmall}>JPG/PNG, preferably under 1MB</div>
                </div>
              </label>
              <button type="button" onClick={generateAiImage} disabled={generatingImage} style={{...sx.secondaryBtn, marginTop:10}}>
                {generatingImage ? "Generating image…" : "✨ Generate with AI"}
              </button>

              {imagePreview && (
                <div style={sx.imagePreviewRow}>
                  <img src={imagePreview} alt="Product" style={sx.imageThumb} />
                  <button
                    type="button"
                    onClick={() => {
                      setProductImageFile(null);
                      setImagePreview(null);
                    }}
                    style={sx.removeImgBtn}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <div style={sx.actions}>
              <button
                onClick={handleSubmit}
                disabled={submitting || savingOptions}
                style={{
                  ...sx.primaryBtn,
                  opacity: submitting || savingOptions ? 0.85 : 1,
                }}
              >
                {savingOptions
                  ? "Saving options…"
                  : editingProductId
                  ? "Update Product"
                  : "Save Product"}
              </button>
              <button onClick={() => embedded && onCancel ? onCancel() : resetForm()} style={sx.ghostBtn}>
                {embedded ? "Cancel" : "Cancel / Reset"}
              </button>
            </div>
          </div>

          <div style={sx.rightCol}>
            <div style={sx.cardSection}>
              <div style={sx.sectionHeader}>
                <div style={sx.sectionTitle}>Variants</div>
                <input
                  type="checkbox"
                  checked={variantsEnabled}
                  onChange={(e) => setVariantsEnabled(e.target.checked)}
                />
              </div>

              {variantsEnabled ? (
                <>
                  {variants.map((variant) => (
                    <div key={variant.id} style={sx.variantRow} className="add-product-variant-row">
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <label style={sx.label}>Label</label>
                        <input
                          style={sx.input}
                          placeholder="e.g., Full / Half"
                          value={variant.label}
                          onChange={(e) =>
                            updateVariantField(variant.id, "label", e.target.value)
                          }
                          maxLength={30}
                        />
                      </div>
                      <div style={sx.variantPriceCol}>
                        <label style={sx.label}>Price</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          pattern="[0-9.]*"
                          style={sx.input}
                          placeholder="e.g., 199"
                          value={String(variant.price ?? "")}
                          onChange={(e) =>
                            updateVariantField(variant.id, "price", e.target.value)
                          }
                          maxLength={PRICE_INPUT_MAX_CHARS}
                        />
                      </div>
                      <button
                        type="button"
                        style={sx.iconBtn}
                        onClick={() => setDefaultVariantId(variant.id)}
                        title="Set default"
                      >
                        {defaultVariantId === variant.id ? "Default" : "Set"}
                      </button>
                      <button
                        type="button"
                        style={sx.iconBtnDanger}
                        onClick={() => removeVariantRow(variant.id)}
                        disabled={variants.length === 1}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <button type="button" onClick={addVariantRow} style={sx.secondaryBtn}>
                    + Add Variant
                  </button>
                  <div style={sx.helperMuted}>
                    Select one variant as default (used for base price).
                  </div>
                </>
              ) : (
                <div style={sx.helperMuted}>
                  Toggle on if product has multiple variants.
                </div>
              )}
            </div>

            <div style={sx.cardSection}>
              <div style={sx.sectionHeader}>
                <div style={sx.sectionTitle}>Add-ons</div>
                <input
                  type="checkbox"
                  checked={addonsEnabled}
                  onChange={(e) => setAddonsEnabled(e.target.checked)}
                />
              </div>

              {addonsEnabled ? (
                <>
                  {addonLoading ? (
                    <div style={sx.helperMuted}>Loading add-ons…</div>
                  ) : null}

                  {addonList.length ? (
                    addonList.map((addon) => {
                      const selected = selectedAddonIds.includes(addon.id);
                      const displayPrice =
                        addon?.priceaftertax ?? addon?.price ?? 0;
                      return (
                        <div key={addon.id} style={sx.addonRow}>
                          <label style={sx.addonLeft}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleAddonSelection(addon.id)}
                            />
                            <span style={sx.addonName}>{addon.name}</span>
                          </label>
                          <span style={sx.addonPrice}>
                            {fmtMoney(displayPrice)}
                          </span>
                          <button
                            type="button"
                            style={sx.iconBtn}
                            onClick={() => startEditAddon(addon)}
                            title="Edit"
                          >
                            ✎
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div style={sx.helperMuted}>No add-ons</div>
                  )}

                  {editingAddonId ? (
                    <div style={sx.addonEditWrap}>
                      <div style={sx.inlineRow}>
                        <div style={{ flex: 1 }}>
                          <label style={sx.label}>Addon name</label>
                          <input
                            style={sx.input}
                            value={editAddonName}
                            onChange={(e) => setEditAddonName(e.target.value)}
                          />
                        </div>
                        <div style={sx.variantPriceCol}>
                          <label style={sx.label}>Price</label>
                          <input
                            style={sx.input}
                            value={editAddonPrice}
                            onChange={(e) =>
                              setEditAddonPrice(sanitizePriceInput(e.target.value))
                            }
                          />
                        </div>
                      </div>
                      <div style={sx.inlineRow}>
                        <button
                          type="button"
                          style={sx.secondaryBtn}
                          onClick={handleUpdateAddon}
                          disabled={savingAddonEdit}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          style={sx.ghostBtn}
                          onClick={cancelEditAddon}
                          disabled={savingAddonEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div style={sx.inlineRow}>
                    <div style={{ flex: 1 }}>
                      <label style={sx.label}>Addon name</label>
                      <input
                        style={sx.input}
                        value={newAddonName}
                        onChange={(e) => setNewAddonName(e.target.value)}
                        placeholder="e.g., Extra Cheese"
                      />
                    </div>
                    <div style={sx.variantPriceCol}>
                      <label style={sx.label}>Price</label>
                      <input
                        style={sx.input}
                        value={newAddonPrice}
                        onChange={(e) =>
                          setNewAddonPrice(sanitizePriceInput(e.target.value))
                        }
                        placeholder="e.g., 20"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCreateAddon}
                    style={sx.secondaryBtn}
                    disabled={addonLoading}
                  >
                    + Create Addon
                  </button>
                  <div style={sx.helperMuted}>
                    Select addons to attach to this product.
                  </div>
                </>
              ) : (
                <div style={sx.helperMuted}>
                  Toggle on to add addons to this product.
                </div>
              )}
            </div>

            <div style={sx.cardSection}>
              <div style={sx.sectionHeader}>
                <div style={sx.sectionTitle}>Link Inventory</div>
                <input
                  type="checkbox"
                  checked={inventoryLinkEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setInventoryLinkEnabled(enabled);
                    if (!enabled) {
                      setSelectedInventoryItemId("");
                    }
                  }}
                />
              </div>

              {inventoryLinkEnabled ? (
                <>
                  <div style={sx.fieldBlock}>
                    <label style={sx.label}>PCS Inventory Item</label>
                    <select
                      value={selectedInventoryItemId}
                      onChange={(e) => setSelectedInventoryItemId(e.target.value)}
                      style={sx.select}
                      disabled={inventoryItemsLoading}
                    >
                      <option value="">
                        {inventoryItemsLoading
                          ? "Loading inventory items..."
                          : "Select a PCS inventory item"}
                      </option>
                      {inventoryPickerItems.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item?.is_active === false
                            ? `${item.name} (Inactive)`
                            : item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={sx.helperMuted}>
                    Only inventory items with base unit PCS can be linked to a
                    product.
                  </div>
                  <div style={sx.helperMuted}>
                    When this product is sold, the linked inventory quantity
                    will be reduced automatically.
                  </div>
                  {selectedInventoryItemId ? (
                    <button
                      type="button"
                      onClick={() => setSelectedInventoryItemId("")}
                      style={sx.linkBtn}
                    >
                      Remove Link
                    </button>
                  ) : null}
                </>
              ) : (
                <div style={sx.helperMuted}>
                  Toggle on to auto-deduct stock from a linked PCS inventory
                  item.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!embedded ? <>
      {/* Tools */}
      <div style={sx.toolsBar} className="add-product-toolsbar">
        <div style={sx.searchWrap}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            style={{ opacity: 0.6 }}
          >
            <path
              fill="currentColor"
              d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19zM9.5 14c-2.49 0-4.5-2.01-4.5-4.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14"
            />
          </svg>
          <input
            placeholder="Search product/name/tax…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            style={sx.searchInput}
          />
        </div>

        <select
          value={catFilter}
          onChange={(e) => {
            setCatFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={sx.filterSelect}
        >
          <option value="">All Categories</option>
          {(categoryList || []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.productcategoryname}
            </option>
          ))}
        </select>

        <div style={sx.toolsEnd} className="add-product-tools-end">
          <button
            type="button"
            onClick={toggleSelectAllFiltered}
            style={sx.secondaryBtn}
          >
            {allFilteredSelected ? "Clear Selection" : "Select All"}
          </button>
          <button
            type="button"
            onClick={handleDeleteSelectedProducts}
            style={{
              ...sx.dangerBtn,
              opacity: bulkDeleting || !selectedProductIds.length ? 0.65 : 1,
              cursor: bulkDeleting || !selectedProductIds.length ? "not-allowed" : "pointer",
            }}
            disabled={bulkDeleting || !selectedProductIds.length}
          >
            {bulkDeleting
              ? "Deleting..."
              : `Delete Selected (${selectedProductIds.length})`}
          </button>
          <span style={{ color: "#334155", fontWeight: 700 }}>Show</span>
          <select
            value={productsPerPage}
            onChange={(e) => {
              setProductsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            style={sx.filterSelect}
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={sx.tableWrap} className="add-product-table-wrap">
        <table style={sx.table} className="add-product-table">
          <thead>
            <tr>
              <th style={sx.thCheck}>
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAllFiltered}
                  aria-label="Select all products"
                />
              </th>
              <th style={sx.th}>Name</th>
              <th style={sx.th}>Category</th>
              <th style={sx.th}>Price</th>
              <th style={sx.th}>{taxDisplayLabel} %</th>
              <th style={sx.th}>Tax Type</th>
              <th style={sx.thRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={sx.tdCenter}>
                  Loading…
                </td>
              </tr>
            ) : paginatedProducts.length === 0 ? (
              <tr>
                <td colSpan={7} style={sx.tdCenter}>
                  No products found.
                </td>
              </tr>
            ) : (
              paginatedProducts.map((item, idx) => (
                <tr
                  key={getProductKey(item) || `row-${idx}`}
                  style={{ background: idx % 2 ? "#FBFDFF" : "#fff" }}
                >
                  {(() => {
                    const itemId = getProductKey(item);
                    const isDeleting = Boolean(itemId) && String(deletingProductId) === itemId;
                    const isSelected = Boolean(itemId) && selectedProductIdSet.has(itemId);
                    const rawTaxType = String(item.withorwithoutgst || "").toLowerCase();
                    const withTax =
                      rawTaxType === "withgst" ||
                      rawTaxType === "withtax" ||
                      rawTaxType === "with";
                    const withoutTax =
                      rawTaxType === "withoutgst" ||
                      rawTaxType === "withouttax" ||
                      rawTaxType === "without";
                    const gstPercent = Number(
                      item.gstpercetage ?? item.gstpercentage ?? item.gst_percent ?? 0
                    );
                    const gstPercentLabel =
                      Number.isFinite(gstPercent) && gstPercent > 0 ? `${gstPercent}%` : "0%";
                    const taxLabel = withTax
                      ? `With ${taxDisplayLabel}`
                      : withoutTax
                      ? `Without ${taxDisplayLabel}`
                      : "Not set";
                    return (
                      <>
                  <td style={sx.tdCheck}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectProduct(itemId)}
                      aria-label={`Select ${item?.name || "product"}`}
                    />
                  </td>
                  <td style={sx.td}>
                    <div style={{ fontWeight: 800, color: "var(--app-text)" }}>
                      {item.name}
                      {item.__offlinePending || item.__localOnly ? (
                        <span style={{ marginLeft: 8, fontSize: 11, color: "#b45309", fontWeight: 700 }}>
                          {item.__localOnly ? "Offline only" : "Pending sync"}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ color: "var(--app-text-muted)", fontSize: 12 }}>
                      {genericTax
                        ? `${taxDisplayLabel} ${fmtMoney(item.taxamount)}`
                        : `SGST ${fmtMoney(item.sgst)} / CGST ${fmtMoney(item.cgst)}`}
                    </div>
                  </td>
                  <td style={sx.td}>
                    {categoryList.find(
                      (c) => String(c.id) === String(item.productcategoryid)
                    )?.productcategoryname || "-"}
                  </td>
                  <td style={sx.td}>{fmtMoney(item.price)}</td>
                  <td style={sx.td}>{gstPercentLabel}</td>
                  <td style={sx.td}>
                    <span
                      style={{
                        ...sx.badge,
                        background: withTax ? "#E0EAFF" : withoutTax ? "#ECFDF5" : "#F1F5F9",
                        color: withTax ? "var(--app-primary)" : withoutTax ? "#047857" : "#475569",
                        borderColor: withTax
                          ? "var(--app-primary-soft)"
                          : withoutTax
                          ? "#D1FAE5"
                          : "#CBD5E1",
                      }}
                    >
                      {taxLabel}
                    </span>
                  </td>
                  <td style={sx.tdRight}>
                    <div style={sx.actionBtns}>
                      <button style={sx.smallBtn} onClick={() => editProduct(item)} disabled={Boolean(item.__offlinePending)}>
                        Edit
                      </button>
                      <button
                        style={{
                          ...sx.dangerBtn,
                          padding: "9px 12px",
                          opacity: isDeleting ? 0.65 : 1,
                        }}
                        onClick={() => handleDeleteProduct(item)}
                        disabled={isDeleting || Boolean(item.__offlinePending)}
                      >
                        {isDeleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </td>
                      </>
                    );
                  })()}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={sx.pagination}>
        {Array.from({ length: totalPages }).map((_, idx) => {
          const page = idx + 1;
          const active = page === currentPage;
          return (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              style={{ ...sx.pageBtn, ...(active ? sx.pageBtnActive : {}) }}
            >
              {page}
            </button>
          );
        })}
      </div>
      </> : null}

      {/* Add Category Modal */}
      {addCategoryOpen && (
        <div style={sx.modalBackdrop}>
          <div style={sx.modalCard}>
            <div style={sx.modalTitle}>Add Category</div>
            <input
              style={sx.input}
              placeholder="Category name"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
            <div style={sx.modalActions}>
              <button
                onClick={handleAddCategory}
                style={sx.primaryBtn}
                disabled={savingCategory}
              >
                {savingCategory ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => setAddCategoryOpen(false)}
                style={sx.ghostBtn}
                disabled={savingCategory}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Used product delete confirmation — matches the mobile safety flow. */}
      {usedProductDeleteTarget && (
        <div style={sx.usedDeleteBackdrop} role="presentation">
          <div
            style={sx.usedDeleteCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="used-product-delete-title"
          >
            <div style={sx.usedDeleteIcon} aria-hidden="true">!</div>
            <div id="used-product-delete-title" style={sx.usedDeleteTitle}>
              Product already used
            </div>
            <div style={sx.usedDeleteProductName}>
              {usedDeleteIsBulk
                ? `${usedDeleteSelectedCount} selected • ${usedDeleteProducts.length} need confirmation`
                : usedDeleteProducts[0]?.name || "Product"}
            </div>
            <div style={sx.usedDeleteMessage}>
              {usedDeleteIsBulk
                ? `${usedDeleteProducts.length} of these products are present in previous bills. `
                : "This product is present in previous bills. "}
              Historical bills will remain unchanged, but the
              {usedDeleteIsBulk ? " selected products" : " product"} will be removed
              from the active product list.
            </div>

            <label
              style={{
                ...sx.usedDeleteAgreement,
                ...(usedProductDeleteAgreed ? sx.usedDeleteAgreementActive : {}),
              }}
            >
              <input
                type="checkbox"
                checked={usedProductDeleteAgreed}
                onChange={(event) => setUsedProductDeleteAgreed(event.target.checked)}
                disabled={usedDeleteBusy}
              />
              <span>
                I understand and want to delete {usedDeleteIsBulk ? "these products" : "this product"}
              </span>
            </label>

            <div style={sx.usedDeleteActions}>
              <button
                type="button"
                style={sx.usedDeleteCancelBtn}
                disabled={usedDeleteBusy}
                onClick={() => {
                  setUsedProductDeleteTarget(null);
                  setUsedProductDeleteAgreed(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  ...sx.usedDeleteConfirmBtn,
                  ...(!usedProductDeleteAgreed || usedDeleteBusy
                    ? sx.usedDeleteConfirmBtnDisabled
                    : {}),
                }}
                disabled={!usedProductDeleteAgreed || usedDeleteBusy}
                onClick={confirmUsedProductDelete}
              >
                {usedDeleteBusy
                  ? "Deleting…"
                  : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            ...sx.toast,
            background:
              toast.type === "success"
                ? "#16a34a"
                : toast.type === "error"
                ? "#b91c1c"
                : BRAND,
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AddProduct;

/* ==================== styles ==================== */
const sx = {
  embeddedPage: {
    background: "var(--app-bg)",
    fontFamily: "Manrope, Nunito Sans, system-ui, sans-serif",
    padding: 0,
  },
  embeddedCard: {
    background: "var(--app-surface)",
    padding: 18,
  },
  page: {
    minHeight: "100vh",
    background: "var(--app-bg)",
    fontFamily:
      "Manrope, Nunito Sans, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
    padding: 18,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(135deg, rgba(var(--app-primary-rgb), 1), rgba(0,57,98,1))",
    color: "#fff",
    borderRadius: 18,
    padding: "16px 18px",
    boxShadow: "0 14px 30px rgba(var(--app-primary-rgb), .2)",
    position: "sticky",
    top: 10,
    zIndex: 5,
  },
  logoBadge: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: "rgba(255,255,255,.2)",
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
    fontSize: 18,
  },
  title: { fontSize: 20, fontWeight: 800, lineHeight: 1.1 },
  subtitle: { fontSize: 12, opacity: 0.9, marginTop: 3 },
  headerRight: { display: "flex", alignItems: "center", gap: 10 },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(255,255,255,.18)",
    border: "1px solid rgba(255,255,255,.28)",
    fontWeight: 800,
  },
  pillDot: {
    width: 8,
    height: 8,
    borderRadius: 99,
    background: "var(--app-surface)",
    opacity: 0.8,
  },

  card: {
    marginTop: 14,
    background: "var(--app-surface)",
    borderRadius: 18,
    padding: 18,
    border: "1px solid #E5E7EB",
    boxShadow: "0 14px 34px rgba(15,23,42,.08)",
  },
  formGrid: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    marginBottom: 6,
  },
  formLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 360px",
    gap: 16,
    alignItems: "start",
  },
  leftCol: { minWidth: 0, display: "grid", gap: 14 },
  rightCol: { minWidth: 0, display: "grid", gap: 14 },
  inlineRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  fieldBlock: { display: "grid", gap: 6 },
  actions: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  label: {
    fontSize: 12,
    color: "#334155",
    fontWeight: 700,
    marginBottom: 6,
    display: "block",
  },
  select: {
    width: "100%",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    padding: "10px 12px",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    outline: "none",
  },
  input: {
    width: "100%",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    padding: "10px 12px",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    outline: "none",
    boxShadow: "inset 0 1px 2px rgba(15,23,42,.05)",
  },
  switchRow: { display: "flex", alignItems: "center", height: 40 },
  pillsRow: { display: "flex", gap: 8, alignItems: "center" },
  linkBtn: {
    marginTop: 6,
    border: "none",
    background: "transparent",
    color: BRAND,
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "left",
    padding: 0,
  },
  pillBtn: {
    padding: "8px 12px",
    borderRadius: 999,
    border: "1px solid #E2E8F0",
    background: "#F1F5F9",
    fontWeight: 800,
    cursor: "pointer",
  },
  pillBtnActive: { background: "#EEF2FF", borderColor: "#C7D2FE" },

  previewBox: {
    gridColumn: "1 / -1",
    background: "#F8FAFC",
    borderRadius: 12,
    padding: 12,
    border: "1px solid #E2E8F0",
  },
  previewLine: { color: "var(--app-text)", fontSize: 13, marginTop: 2 },
  previewHint: { color: "var(--app-text-muted)", fontSize: 12, marginTop: 6 },
  cardSection: {
    marginTop: 10,
    background: "var(--app-surface-raised)",
    borderRadius: 14,
    padding: 14,
    border: "1px solid #DDE5F0",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  sectionTitle: { fontWeight: 900, color: "var(--app-text)", fontSize: 18 },
  helperMuted: { color: "var(--app-text-muted)", fontSize: 13, marginTop: 6 },
  variantRow: {
    display: "flex",
    gap: 10,
    alignItems: "flex-end",
    flexWrap: "wrap",
    width: "100%",
    overflow: "hidden",
    marginBottom: 8,
  },
  variantPriceCol: {
    width: 170,
    minWidth: 140,
  },
  iconBtn: {
    height: 42,
    minWidth: 94,
    borderRadius: 10,
    border: "1px solid var(--app-primary-hover)",
    background: "var(--app-primary-soft)",
    color: "var(--app-primary)",
    cursor: "pointer",
    fontWeight: 800,
    fontSize: 12,
  },
  iconBtnDanger: {
    height: 42,
    minWidth: 44,
    borderRadius: 10,
    border: "1px solid #FCA5A5",
    background: "#FEF2F2",
    color: "#B91C1C",
    cursor: "pointer",
    fontWeight: 800,
  },
  addonRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 0",
    borderBottom: "1px solid #E5E7EB",
  },
  addonLeft: { display: "flex", alignItems: "center", gap: 8, flex: 1 },
  addonName: { fontWeight: 700, color: "var(--app-text)" },
  addonPrice: { fontWeight: 700, color: "#334155" },
  addonEditWrap: {
    marginTop: 10,
    padding: 10,
    borderRadius: 10,
    border: "1px dashed #CBD5E1",
    background: "var(--app-surface)",
  },

  // ✅ image styles
  imageSection: {
    marginTop: 12,
    marginBottom: 8,
  },
  uploadBox: {
    border: "1px dashed #CBD5E1",
    borderRadius: 12,
    padding: "10px 12px",
    background: "#F8FAFC",
    cursor: "pointer",
  },
  imagePreviewRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 10,
  },
  imageThumb: {
    width: 64,
    height: 64,
    borderRadius: 12,
    objectFit: "cover",
    background: "#E5E7EB",
  },
  removeImgBtn: {
    border: "none",
    background: "transparent",
    color: "#EF4444",
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
  },
  mutedSmall: { color: "var(--app-text-muted)", fontSize: 11 },
  mutedRight: { color: "var(--app-text-muted)", fontSize: 12, textAlign: "right" },

  primaryBtn: {
    height: 46,
    borderRadius: 12,
    background: "linear-gradient(135deg, var(--app-primary), var(--app-primary-hover))",
    color: "#fff",
    fontWeight: 900,
    border: "none",
    padding: "0 16px",
    cursor: "pointer",
    marginTop: 8,
  },
  ghostBtn: {
    height: 44,
    borderRadius: 12,
    background: "#F8FAFC",
    color: "var(--app-text)",
    border: "1px solid #E5E7EB",
    fontWeight: 800,
    padding: "0 14px",
    cursor: "pointer",
    marginLeft: 8,
  },
  secondaryBtn: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    background: "var(--app-surface)",
    color: "var(--app-text)",
    fontWeight: 800,
    padding: "0 14px",
    cursor: "pointer",
  },

  toolsBar: {
    marginTop: 16,
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    padding: 14,
    borderRadius: 16,
    border: "1px solid #E5E7EB",
    background: "var(--app-surface)",
    boxShadow: "0 10px 22px rgba(15,23,42,.06)",
  },
  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "var(--app-surface)",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #E5E7EB",
    minWidth: 260,
  },
  searchInput: {
    border: "none",
    outline: "none",
    background: "transparent",
    width: 240,
    color: "var(--app-text)",
    fontSize: 14,
  },
  filterSelect: {
    border: "1px solid #E5E7EB",
    background: "var(--app-surface)",
    borderRadius: 10,
    padding: "9px 12px",
    minHeight: 42,
  },
  toolsEnd: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  tableWrap: {
    marginTop: 12,
    background: "var(--app-surface)",
    borderRadius: 18,
    border: "1px solid #E5E7EB",
    overflow: "auto",
    boxShadow: "0 12px 28px rgba(15,23,42,.06)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  th: {
    textAlign: "left",
    padding: "14px 14px",
    background: "#F8FAFC",
    color: "var(--app-text)",
    borderBottom: "1px solid #E5E7EB",
    fontWeight: 800,
  },
  thCheck: {
    textAlign: "center",
    padding: "14px 10px",
    background: "#F8FAFC",
    color: "var(--app-text)",
    borderBottom: "1px solid #E5E7EB",
    fontWeight: 800,
    width: 48,
  },
  thRight: {
    textAlign: "right",
    padding: "14px 14px",
    background: "#F8FAFC",
    color: "var(--app-text)",
    borderBottom: "1px solid #E5E7EB",
    fontWeight: 800,
    width: 220,
  },
  td: {
    padding: "14px 14px",
    borderBottom: "1px solid #F1F5F9",
    verticalAlign: "top",
  },
  tdCheck: {
    padding: "14px 10px",
    borderBottom: "1px solid #F1F5F9",
    textAlign: "center",
    verticalAlign: "middle",
  },
  tdRight: {
    padding: "14px 14px",
    borderBottom: "1px solid #F1F5F9",
    textAlign: "right",
  },
  tdCenter: { padding: "14px", textAlign: "center", color: "var(--app-text-muted)" },
  badge: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 999,
    fontWeight: 800,
    border: "1px solid",
    fontSize: 12,
    textTransform: "lowercase",
  },
  smallBtn: {
    padding: "9px 14px",
    borderRadius: 11,
    background: "#F1F5F9",
    border: "1px solid #CBD5E1",
    fontWeight: 800,
    cursor: "pointer",
    color: "var(--app-text)",
  },
  dangerBtn: {
    padding: "9px 14px",
    borderRadius: 11,
    background: "#FEF2F2",
    border: "1px solid #FCA5A5",
    fontWeight: 800,
    cursor: "pointer",
    color: "#B91C1C",
  },
  warningBtn: {
    padding: "9px 14px",
    borderRadius: 11,
    background: "#FFF7ED",
    border: "1px solid #FDBA74",
    fontWeight: 800,
    cursor: "pointer",
    color: "#C2410C",
  },
  actionBtns: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    flexWrap: "wrap",
  },

  pagination: {
    display: "flex",
    gap: 6,
    marginTop: 12,
    justifyContent: "center",
  },
  pageBtn: {
    padding: "6px 10px",
    borderRadius: 10,
    border: "1px solid #E5E7EB",
    background: "var(--app-surface)",
    cursor: "pointer",
    fontWeight: 800,
  },
  pageBtnActive: { background: BRAND, color: "#fff", borderColor: BRAND },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.45)",
    display: "grid",
    placeItems: "center",
    zIndex: 80,
  },
  modalCard: {
    background: "var(--app-surface)",
    borderRadius: 14,
    padding: 16,
    width: "min(420px, 92vw)",
    border: "1px solid #E5E7EB",
    boxShadow: "0 10px 30px rgba(0,0,0,.2)",
  },
  modalTitle: { fontWeight: 800, fontSize: 16, marginBottom: 10 },
  modalActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
  },
  usedDeleteBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 100,
    display: "grid",
    placeItems: "center",
    padding: 16,
    background: "rgba(15, 23, 42, 0.58)",
  },
  usedDeleteCard: {
    width: "min(440px, 100%)",
    padding: 22,
    borderRadius: 18,
    border: "1px solid #FED7AA",
    background: "var(--app-surface)",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.3)",
    textAlign: "center",
  },
  usedDeleteIcon: {
    width: 54,
    height: 54,
    margin: "0 auto 12px",
    display: "grid",
    placeItems: "center",
    borderRadius: 999,
    background: "#FFF7ED",
    border: "1px solid #FDBA74",
    color: "#B45309",
    fontSize: 28,
    fontWeight: 900,
  },
  usedDeleteTitle: { color: "var(--app-text)", fontSize: 20, fontWeight: 900 },
  usedDeleteProductName: {
    marginTop: 6,
    color: "#B45309",
    fontSize: 15,
    fontWeight: 900,
  },
  usedDeleteMessage: {
    marginTop: 12,
    color: "var(--app-text-muted)",
    fontSize: 13,
    lineHeight: 1.55,
  },
  usedDeleteAgreement: {
    marginTop: 16,
    padding: "12px 13px",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    border: "1px solid #CBD5E1",
    borderRadius: 12,
    background: "#F8FAFC",
    color: "#334155",
    fontSize: 13,
    fontWeight: 750,
    lineHeight: 1.4,
    textAlign: "left",
    cursor: "pointer",
  },
  usedDeleteAgreementActive: {
    borderColor: "#60A5FA",
    background: "var(--app-primary-soft)",
    color: "#1E3A8A",
  },
  usedDeleteActions: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 16 },
  usedDeleteCancelBtn: {
    minHeight: 44,
    borderRadius: 11,
    border: "1px solid #CBD5E1",
    background: "var(--app-surface)",
    color: "#334155",
    fontWeight: 850,
    cursor: "pointer",
  },
  usedDeleteConfirmBtn: {
    minHeight: 44,
    borderRadius: 11,
    border: "1px solid #B91C1C",
    background: "#B91C1C",
    color: "#FFFFFF",
    fontWeight: 850,
    cursor: "pointer",
  },
  usedDeleteConfirmBtnDisabled: { opacity: 0.48, cursor: "not-allowed" },
  toast: {
    position: "fixed",
    bottom: 20,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: 12,
    fontWeight: 800,
    boxShadow: "0 10px 20px rgba(0,0,0,.2)",
    zIndex: 70,
  },
};

if (!document.getElementById("add-product-page-styles")) {
  const styleTag = document.createElement("style");
  styleTag.id = "add-product-page-styles";
  styleTag.innerHTML = `
    @media (max-width: 1100px){
      .add-product-form-layout{ grid-template-columns: 1fr; }
    }
    .add-product-table tbody tr:hover{
      background: #F3F8FF !important;
    }
    @media (max-width: 860px){
      .add-product-table{ min-width: 760px; }
    }
    @media (max-width: 760px){
      .add-product-tools-end{
        margin-left: 0 !important;
      }
      .add-product-toolsbar{
        gap: 8px !important;
      }
    }
  `;
  document.head.appendChild(styleTag);
}
